"use client"

import { Menu, MenuItem, MenuList, Paper } from "@mui/material";
import { useSession } from "next-auth/react";
import { useState } from "react";
import { FaRegUserCircle } from "react-icons/fa";
import { MdOutlineMailOutline } from "react-icons/md";
import { PiSignOutFill } from "react-icons/pi";
import { IoIosArrowUp } from "react-icons/io";
import { Logout } from "@/helpers/auth/Logout";
import { darkMode } from "@/store/darkMode/darkMode-store";

interface Props {
    collapsed?: boolean;
}

export const UserMenu = ({ collapsed = false }: Props) => {
    const { data: session } = useSession();
    const isDark = darkMode((s) => s.isDark);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const surface = isDark ? "#151c2e" : "#ffffff";
    const hover = isDark ? "#1c253c" : "#eef4fc";
    const fg = isDark ? "#e2e8f0" : "#1f2937";

    const initials = (session?.user?.name ?? "?")
        .split(" ")
        .slice(0, 2)
        .map((p: string) => p[0])
        .join("")
        .toUpperCase();

    return (
        <div>
            <button
                type="button"
                onClick={handleClick}
                title={collapsed ? session?.user?.name ?? "Cuenta" : undefined}
                className={`flex w-full items-center rounded-lg text-xs transition-colors motion-reduce:transition-none hover:bg-[var(--nav-hover-bg)]
                    ${collapsed ? "justify-center p-2" : "justify-between gap-2 p-2"}`}
            >
                <span className="flex min-w-0 items-center gap-2">
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-[var(--nav-accent)] text-[11px] font-semibold text-white">
                        {initials}
                    </span>
                    {!collapsed && (
                        <span className="min-w-0 text-left">
                            <span className="block truncate text-[var(--nav-fg-strong)]">{session?.user?.name}</span>
                            <span className="block truncate text-[10px] leading-tight text-[var(--nav-fg)]">
                                {session?.user?.email}
                            </span>
                        </span>
                    )}
                </span>
                {!collapsed && <IoIosArrowUp className="shrink-0 text-[var(--nav-fg)]" />}
            </button>

            <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                MenuListProps={{ sx: { backgroundColor: surface, py: 0.5 } }}
            >
                <Paper elevation={0} sx={{ minWidth: 240, backgroundColor: surface }}>
                    <MenuList sx={{ backgroundColor: surface }}>
                        {[
                            { icon: <FaRegUserCircle size={18} />, label: "Mi perfil", onClick: handleClose },
                            { icon: <MdOutlineMailOutline size={18} />, label: "Mensajes", onClick: handleClose },
                            { icon: <PiSignOutFill size={18} />, label: "Cerrar sesión", onClick: () => Logout() },
                        ].map((it) => (
                            <MenuItem
                                key={it.label}
                                onClick={it.onClick}
                                sx={{ backgroundColor: surface, "&:hover": { backgroundColor: hover } }}
                            >
                                <span className="flex items-center gap-2 text-[12px]" style={{ color: fg }}>
                                    {it.icon}
                                    {it.label}
                                </span>
                            </MenuItem>
                        ))}
                    </MenuList>
                </Paper>
            </Menu>
        </div>
    );
};
